import React, { useState, useEffect } from "react";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import DatePicker from "@services/ui-components/src/DatePicker/DatePicker";
import { useNavigate, useParams } from "react-router-dom";
import { Col, OverlayTrigger, Tooltip, Button, Row, Popover } from "react-bootstrap";
import i18next from "i18next";
import { ErrorModalComponent } from "@me-pos/error-modal";
import { useReactiveVar } from "@apollo/client";
import { SpinnerLoader, TableHeader } from "@services/ui-components";
import VioletBadge from "@services/ui-components/src/VioletBadge/VioletBadge";
import CustomCreatableReactSelect from "@services/ui-components/src/select/CustomCreatableReactSelect";
import PhonePicker from "@services/ui-components/src/PhonePicker/PhonePicker";
import ImageCrop from "@services/ui-components/src/ImageCrop/ImageCrop";
import DropItem from "@services/ui-components/src/DropItem/DropItem";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import ProgressBar from "@services/ui-components/src/ProgressBar/ProgressBar";
import DropPhotosMultiply from "../DropPhotosMultiply/DropPhotosMultiply";
import ErrorService from "../../services/ErrorService";
import CustomReactSelect from "@services/ui-components/src/select/CustomReactSelect";
import { isRefetchBranches } from "@me-team/host/main/branches/state";
import {
    useGetCountryGeoQuery,
    useUpdateCurrentBranchMutation,
} from "@me-team/host/main/branches/graphql/branches.hooks";
import {
    useGetUserForCreateOwnerPageQuery,
    useUpdateUserMutation,
} from "@me-team/host/main/globalVar/graphql/globalVar.hooks";
import {
    currentBranchEmployeeIdVar,
    currentBranchIdVar,
    currentCompanyIdVar,
    currentUserEmailVar,
    userIdVar,
} from "@me-team/host/main/globalVar/state";
import { refetchUserDataMenu } from "@me-team/host/src/apollo/apollo-client";
import {
    useCreateOwnerMutation,
    useGetRolesQuery,
    useGetSpecSpecializationQuery,
} from "@me-team/host/main/addEmployee/graphql/addEmployee.hooks";
import { addEmployeeFormValidationSchema } from "../AddEmployee/constants/constants";
import { useScrollToError } from "@me-team/host/src/utils/useScrollToError";
import {
    useGetCompanyIdForCreateOwnerQuery,
    useGetEmployeesPositionsForCreateOwnerQuery,
} from "@me-team/host/main/createOwner/graphql/createOwner.hooks";

interface IStep {
    key: string;
    label: string;
}

const steps = (t: any): IStep[] => [
    { key: "registration", label: t("Registration") },
    { key: "create-company", label: t("Create company") },
    { key: "create-owner", label: t("Create owner") },
];

const OwnerCreation: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentBranchId = useReactiveVar(currentBranchIdVar);
    const userId = useReactiveVar(userIdVar);
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const navigate = useNavigate();
    const stepsArray = steps(t);

    const [startDate, setStartDate] = useState<Date | null>(null);
    const [date] = useState("");
    const [photosQualification, setPhotosQualification] = useState<(File | null)[]>([null]);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<null | any>(null);
    const currentUserEmail = useReactiveVar(currentUserEmailVar);

    const [updateUpdateUserAndBranch] = useUpdateUserMutation();

    useEffect(() => {
        const updateAndRefetch = async () => {
            if (currentCompanyId && userId && !currentBranchId) {
                try {
                    await updateUpdateUserAndBranch({
                        variables: {
                            companyId: Number(currentCompanyId),
                            userId: Number(userId),
                            input: {
                                currentBranch: Number(branchId),
                            },
                            branchId: Number(branchId),
                            isCompanyNew: true,
                        },
                    });
                    await refetchUserDataMenu();
                } catch (error) {
                    console.error("Error branch:", error);
                }
            }
        };
        updateAndRefetch();
    }, [currentCompanyId, branchId, currentBranchId, userId, updateUpdateUserAndBranch]);

    const { data: roles, loading: loadingRoles } = useGetRolesQuery({
        variables: {
            branchId: Number(branchId),
            page: 1,
            itemsPerPage: 10000,
        },
        skip: !currentCompanyId,
        onError: (error) => console.error(error),
    });
    const {
        data: user,
        loading: loadingUser,
        refetch: refetchUser,
    } = useGetUserForCreateOwnerPageQuery({
        skip: !currentBranchId || !currentCompanyId,
        onCompleted: (data) => {
            if (data?.user?.companies[0]?.id && !currentCompanyId) {
                currentCompanyIdVar(String(data.user.companies[0].id));
            }
        },
        context: {
            errorType: "local",
        },
        onError: (error) => {
            console.error("Error fetching user data", error);
        },
    });
    currentCompanyId && refetchUserDataMenu();
    const { data: countryGeoData, loading: loadingCountryGeo } = useGetCountryGeoQuery({});
    const handleChange = (date: Date | null, setFieldValue: (field: string, value: string) => void) => {
        if (date) {
            const formattedDate = new Date(date);
            const year = formattedDate.getFullYear();
            const month = String(formattedDate.getMonth() + 1).padStart(2, "0");
            const day = String(formattedDate.getDate()).padStart(2, "0");
            const formattedDateString = `${year}-${month}-${day}`;
            setStartDate(formattedDate);
            setFieldValue("dateOfBirth", formattedDateString);
        } else {
            setStartDate(null);
            setFieldValue("dateOfBirth", null);
        }
    };

    const { data: positions, loading: loadingPositions } = useGetEmployeesPositionsForCreateOwnerQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
        onError: (error) => {
            console.error(error);
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
    });
    const { data: dataSpecialization, loading: loadingSpecialization } = useGetSpecSpecializationQuery({
        variables: { branchId: Number(branchId) },
    });
    const specialization = dataSpecialization?.employeeSpecializations;
    const { data: companyId, loading: loadingCompanyId } = useGetCompanyIdForCreateOwnerQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
        onError: (error) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
        context: {
            errorType: "global",
        },
    });

    useEffect(() => {
        if (!currentCompanyId) {
            refetchUser();
        }
    }, [refetchUser, currentCompanyId]);

    const [createOwner] = useCreateOwnerMutation();
    const [updateCurrentBranch] = useUpdateCurrentBranchMutation();

    const maxDate = new Date();
    maxDate.setFullYear(maxDate.getFullYear() - 14);
    const countryGeo = countryGeoData?.countryGeo?.country;
    const ownerRole = roles?.role?.roles.find((role) => role.isOwner);
    const ownerPosition = companyId?.user?.company?.ownerPosition;

    const waitForValidIds = async (
        maxRetries: number = 5,
        retryDelay: number = 1000
    ): Promise<{ idCompany: number; branch: number }> => {
        await refetchUser();
        let idCompany = +currentCompanyId;
        let branch = +branchId;

        if (typeof idCompany === "number" && typeof branch === "number") {
            return { idCompany, branch };
        }
        if (maxRetries <= 0) {
            throw new Error("Failed to retrieve valid company or branch ID.");
        }
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        return waitForValidIds(maxRetries - 1, retryDelay);
    };

    if (
        loadingPositions ||
        loadingSpecialization ||
        loadingCompanyId ||
        loadingRoles ||
        loadingUser ||
        typeof !companyId?.user?.company?.ownerPosition?.id === "number" ||
        loadingCountryGeo
    )
        return <Spinner />;

    return (
        <>
            <ProgressBar currentStep={2} steps={stepsArray} />
            <VioletBadge>
                {t(
                    "Required fields are marked with an asterisk *. The data that customers see in the online record form is marked "
                )}{" "}
                <i className="bi bi-info-circle"></i>
            </VioletBadge>
            <TableHeader title={t("Add owner")} />
            <Formik
                initialValues={{
                    company: companyId?.user?.company?.id,
                    name: "",
                    surname: "",
                    patronymic: "",
                    dateOfBirth: date,
                    email: currentUserEmail?.trim() || "",
                    phone: countryGeo?.phoneCode?.code ? `+${countryGeo?.phoneCode?.code}` : "",
                    passportData: "",
                    itn: "",
                    aboutMe: "",
                    employeePosition: companyId?.user?.company?.ownerPosition?.id,
                    specialization: "",
                    role: ownerRole?.id,
                    status: 3,
                    manualBlocking: true,
                    tariffBlocking: true,
                }}
                validationSchema={addEmployeeFormValidationSchema(t)}
                onSubmit={async (values, { setSubmitting, setErrors }) => {
                    try {
                        await refetchUser();
                        const { idCompany, branch } = await waitForValidIds();
                        const ownerRoleId = ownerRole?.id;
                        if (ownerRoleId == null) {
                            setIsErrorModalOpen(500);
                            return;
                        }
                        const cleanedPhotosQualification = photosQualification?.filter((photo) => photo != null);
                        const updatedValues = {
                            ...values,
                            email: values.email?.trim() || "",
                            qualificationConfirmations: [...cleanedPhotosQualification],
                            company: idCompany,
                            employeePosition:
                                values.employeePosition != null ? Number(values.employeePosition) : undefined,
                            role: ownerRoleId,
                        };
                        const response = await createOwner({
                            variables: {
                                companyId: idCompany,
                                input: updatedValues,
                                branchId: branch,
                            },
                            onError: (error) => {
                                console.error(error);
                            },
                        });
                        if (response?.data) {
                            if (!user?.user?.currentBranch?.id) {
                                await updateCurrentBranch({
                                    variables: { companyId: idCompany, branchId: branch },
                                });
                            }
                            const employeeId: number = response?.data?.createOwner?.company.employeesList?.[0].id;
                            isRefetchBranches(true);
                            currentBranchEmployeeIdVar(employeeId);
                            navigate(`/branches/main/branch/${branchId}`, { replace: true, state: { creat: true } });
                        }
                    } catch (error) {
                        console.error("Error creating employee:", error);
                        const graphQLError = error.graphQLErrors?.[0];

                        if (
                            graphQLError?.extensions?.validation?.phone?.[0]?.message ===
                            "This value is not a valid phone number."
                        ) {
                            setErrors({ phone: t("Invalid phone number") });
                        } else if (graphQLError?.message === "Employee already exists") {
                            setErrors({ email: `Email  ${values.email} ${t("already exists in system.")}` });
                        }
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ errors, touched, setFieldValue, values, handleSubmit, isSubmitting, submitCount }) => {
                    /* eslint-disable */
                    // TODO: React Hook "useScrollToError" cannot be called inside a callback. Fix this across all pages where it is used
                    useScrollToError({ submitClickedCount: submitCount, errors });
                    return (
                        <>
                            <Form onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="d-flex flex-column flex-lg-row">
                                        <Col xs={12} lg={9} className="me-0">
                                            <div className="row">
                                                <h4 className="fw-normal">{t("Personal data")}</h4>
                                            </div>
                                            <div className="row mt-sm-4">
                                                <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                    <span className="text-dark fw-bold">
                                                        <small>
                                                            <label
                                                                htmlFor="addOwner-name-field"
                                                                className="mb-0 text-dark form-label required-label"
                                                            >
                                                                {t("Name")}
                                                            </label>
                                                        </small>
                                                    </span>
                                                </div>
                                                <div className="col-sm-9 col-md-10 pe-lg-5">
                                                    <div className="position-relative">
                                                        <Field
                                                            type="text"
                                                            name="name"
                                                            id="addOwner-name-field"
                                                            placeholder={t("Add Name")}
                                                            className={`fs-7 form-control ${errors?.name && touched?.name ? "border-danger" : ""}`}
                                                            maxLength="50"
                                                        />
                                                        <div className="position-absolute top-50 end-0 translate-middle-y ">
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        {t(
                                                                            "This data is displayed in the Online Booking form"
                                                                        )}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <div className=" position-absolute top-50 translate-middle-y end-0 me-3">
                                                                    <i className="bi bi-info-circle"></i>
                                                                </div>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </div>
                                                    <ErrorMessage name="name">
                                                        {(msg) => <p className="fs-7 p-1 text-danger m-0">{msg}</p>}
                                                    </ErrorMessage>
                                                </div>
                                            </div>

                                            <div className="row mt-sm-4">
                                                <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                    <span className="text-dark fw-bold">
                                                        <small>
                                                            <label
                                                                htmlFor="addOwner-surname-field"
                                                                className="mb-0 text-dark form-label required-label"
                                                            >
                                                                {t("Last name")}{" "}
                                                            </label>
                                                        </small>
                                                    </span>
                                                </div>
                                                <div className="col-sm-9 col-md-10 pe-lg-5">
                                                    <div className="position-relative">
                                                        <Field
                                                            type="text"
                                                            name="surname"
                                                            id="addOwner-surname-field"
                                                            placeholder={t("Add Last name")}
                                                            className={`fs-7 form-control ${errors.surname && touched.surname ? "border-danger" : ""}`}
                                                            maxLength="50"
                                                        />
                                                        <div className="position-absolute top-50 end-0 translate-middle-y ">
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        {t(
                                                                            "This data is displayed in the Online Booking form"
                                                                        )}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <div className=" position-absolute top-50 translate-middle-y end-0 me-3">
                                                                    <i className="bi bi-info-circle"></i>
                                                                </div>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </div>
                                                    <ErrorMessage name="surname">
                                                        {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                    </ErrorMessage>
                                                </div>
                                            </div>

                                            <div className="row mt-sm-4">
                                                <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                    <span className="text-dark fw-bold">
                                                        <small>
                                                            <label
                                                                htmlFor="addOwner-middleName-field"
                                                                className="mb-0 text-dark form-label"
                                                            >
                                                                {t("Middle name")}
                                                            </label>
                                                        </small>
                                                    </span>
                                                </div>
                                                <div className="col-sm-9 col-md-10 pe-lg-5">
                                                    <div className="position-relative">
                                                        <Field
                                                            type="text"
                                                            name="patronymic"
                                                            id="addOwner-middleName-field"
                                                            placeholder={t("Add Middle name")}
                                                            className="fs-7 form-control"
                                                            maxLength="50"
                                                        />
                                                    </div>
                                                    <ErrorMessage name="patronymic">
                                                        {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                    </ErrorMessage>
                                                </div>
                                            </div>

                                            <div className="row mt-sm-4">
                                                <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                    <span className="text-dark fw-bold">
                                                        <small>
                                                            <label
                                                                htmlFor="addOwner-date-field"
                                                                className="mb-0 text-dark form-label required"
                                                            >
                                                                {t("Date of birth")}{" "}
                                                            </label>
                                                        </small>
                                                    </span>
                                                </div>
                                                <div className="col-sm-9 col-md-10 pe-lg-5">
                                                    <div className="position-relative">
                                                        <DatePicker
                                                            id="addOwner-date-field"
                                                            name="dateOfBirth"
                                                            className={`form-control ${errors.dateOfBirth && touched.dateOfBirth ? "border-danger" : ""} w-100`}
                                                            wrapperClassName="w-100"
                                                            selected={startDate}
                                                            placeholderText={t("dd.mm.yyyy")}
                                                            onChange={(date) => handleChange(date, setFieldValue)}
                                                            dateFormat="dd.MM.yyyy"
                                                            maxDate={new Date()}
                                                            showYearDropdown
                                                            yearDropdownItemNumber={124}
                                                            scrollableYearDropdown
                                                            isClearable
                                                        />
                                                        <ErrorMessage name="dateOfBirth">
                                                            {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                        </ErrorMessage>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row mt-sm-4">
                                                <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                    <span className="text-dark fw-bold">
                                                        <small>
                                                            <label
                                                                htmlFor="addOwner-email-field"
                                                                className="mb-0 text-dark form-label required-label"
                                                            >
                                                                {t("E-mail")}
                                                            </label>
                                                        </small>
                                                    </span>
                                                </div>
                                                <div className="col-sm-9 col-md-10 pe-lg-5">
                                                    <div className="position-relative">
                                                        <Field
                                                            className={`fs-7 form-control ${errors.email && touched.email ? "border-danger" : ""}`}
                                                            maxLength="50"
                                                            value={currentUserEmail?.trim() || ""}
                                                        />
                                                        <div className="position-absolute top-50 end-0 translate-middle-y ">
                                                            <i className="bi bi-shield-check pe-6"></i>
                                                        </div>
                                                    </div>
                                                    <ErrorMessage name="email">
                                                        {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                    </ErrorMessage>
                                                </div>
                                            </div>

                                            <div className="row mt-sm-4">
                                                <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                    <span className="text-dark fw-bold">
                                                        <small>
                                                            <label
                                                                htmlFor="addOwner-phone-field"
                                                                className="mb-0 text-dark form-label required-label"
                                                            >
                                                                {t("Phone")}{" "}
                                                            </label>
                                                        </small>
                                                    </span>
                                                </div>
                                                <div className="col-sm-9 col-md-10 pe-lg-5">
                                                    <PhonePicker
                                                        name="phone"
                                                        id="addOwner-phone-field"
                                                        value={values.phone}
                                                    />
                                                    <ErrorMessage name="phone">
                                                        {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                    </ErrorMessage>
                                                </div>
                                            </div>

                                            <div className="row mt-sm-4">
                                                <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                    <span className="text-dark fw-bold">
                                                        <small>
                                                            <label
                                                                htmlFor="addOwner-passportData-field"
                                                                className="mb-0 text-dark form-label"
                                                            >
                                                                {t("Passport Data")}{" "}
                                                            </label>
                                                        </small>
                                                    </span>
                                                </div>
                                                <div className="col-sm-9 col-md-10 pe-lg-5">
                                                    <div className="position-relative">
                                                        <Field
                                                            type="text"
                                                            name="passportData"
                                                            placeholder={t("Enter Passport Data")}
                                                            className="fs-7 form-control"
                                                            maxLength="10"
                                                            id="addOwner-passportData-field"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-sm-9 col-md-10 ms-auto">
                                                    <ErrorMessage name="passportData">
                                                        {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                    </ErrorMessage>
                                                    <span className="text-grey fs-7">
                                                        {" "}
                                                        {values.passportData.length}/10
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="row mt-sm-4">
                                                <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                    <span className="text-dark fw-bold">
                                                        <small>
                                                            <label
                                                                htmlFor="addOwner-ssn-field"
                                                                className="mb-0 text-dark form-label"
                                                            >
                                                                {t("SSN (Social Security Number)")}{" "}
                                                            </label>
                                                        </small>
                                                    </span>
                                                </div>
                                                <div className="col-sm-9 col-md-10 pe-lg-5">
                                                    <div className="position-relative">
                                                        <Field
                                                            type="text"
                                                            name="itn"
                                                            placeholder={t("Enter SSN")}
                                                            className="fs-7 form-control"
                                                            maxLength="20"
                                                            id="addOwner-ssn-field"
                                                        />
                                                    </div>
                                                    <ErrorMessage name="itn">
                                                        {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                    </ErrorMessage>
                                                </div>
                                            </div>

                                            <div className="row mt-sm-4">
                                                <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                    <span className="text-dark fw-bold">
                                                        <small>
                                                            <label
                                                                htmlFor="addOwner-about-field"
                                                                className="mb-0 text-dark form-label"
                                                            >
                                                                {t("About")}{" "}
                                                            </label>
                                                        </small>
                                                    </span>
                                                </div>
                                                <div className="col-sm-9 col-md-10 pe-lg-5">
                                                    <div className="position-relative">
                                                        <Field
                                                            id="addOwner-about-field"
                                                            as="textarea"
                                                            name="aboutMe"
                                                            maxLength={400}
                                                            className="form-control pe-5"
                                                            placeholder={t(
                                                                "Enter a short description for the master presentation"
                                                            )}
                                                        />
                                                        <div className="position-absolute top-50 end-0 translate-middle-y">
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        {t(
                                                                            "This data is displayed in the Online Booking form"
                                                                        )}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <div className=" position-absolute top-50 translate-middle-y end-0 me-3">
                                                                    <i className="bi bi-info-circle"></i>
                                                                </div>
                                                            </OverlayTrigger>
                                                        </div>
                                                        <ErrorMessage name="aboutMe">
                                                            {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                        </ErrorMessage>
                                                    </div>
                                                </div>
                                                <div className="col-sm-9 col-md-10 ms-auto">
                                                    <span className="text-grey fs-7">{values.aboutMe.length}/400</span>
                                                </div>
                                            </div>
                                            <div className="row mt-5">
                                                <h4 className="text-dark fw-normal">{t("Professional Information")}</h4>
                                            </div>
                                            <div className="row mt-sm-4">
                                                <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                    <span className="text-dark fw-bold">
                                                        <small>
                                                            <label
                                                                htmlFor="position-field"
                                                                className="mb-0 text-dark form-label required-label"
                                                            >
                                                                {t("Position")}
                                                            </label>
                                                        </small>
                                                    </span>
                                                </div>
                                                <div className="col-sm-9 col-md-10 pe-lg-5">
                                                    <div className="position-relative">
                                                        <div className="d-flex align-items-center w-100">
                                                            <div className=" w-100 position-relative">
                                                                {/*ToDO нужно переделать на ReactSelect*/}
                                                                <CustomReactSelect
                                                                    optionsArray={
                                                                        positions?.user?.company?.employeePositions
                                                                            ?.employeePositions
                                                                    }
                                                                    placeholder={t("Owner")}
                                                                    setField={setFieldValue}
                                                                    fieldName="employeePosition"
                                                                    value="id"
                                                                    isTooltip={true}
                                                                    initialValue={
                                                                        ownerPosition && {
                                                                            id: String(ownerPosition.id),
                                                                            value: ownerPosition.name,
                                                                            label: ownerPosition.name,
                                                                            name: ownerPosition.name,
                                                                        }
                                                                    }
                                                                    name={"position-field"}
                                                                    id={"addOwner-position-field"}
                                                                />
                                                                <div
                                                                    className="position-absolute top-50 end-0 translate-middle-y pe-6"
                                                                    style={{ height: "16px" }}
                                                                >
                                                                    <OverlayTrigger
                                                                        overlay={
                                                                            <Popover>
                                                                                <Popover.Body>
                                                                                    <Row>
                                                                                        <p className="m-0 mb-1">
                                                                                            {t(
                                                                                                "The position is displayed inside the system, and the specialization is in the form of an online record."
                                                                                            )}{" "}
                                                                                        </p>
                                                                                    </Row>
                                                                                </Popover.Body>
                                                                            </Popover>
                                                                        }
                                                                    >
                                                                        <div className=" position-absolute top-50 translate-middle-y end-0 me-3">
                                                                            <i className="bi bi-info-circle"></i>
                                                                        </div>
                                                                    </OverlayTrigger>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <ErrorMessage name="employeePosition">
                                                            {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                        </ErrorMessage>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row mt-sm-4">
                                                <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                    <span className="text-dark fw-bold">
                                                        <small>
                                                            <label
                                                                htmlFor="specialization-field"
                                                                className="mb-0 text-dark form-label text-break required-label"
                                                            >
                                                                {t("Specialization")}
                                                            </label>
                                                        </small>
                                                    </span>
                                                </div>
                                                <div className="col-sm-9 col-md-10 pe-lg-5">
                                                    <div className="position-relative">
                                                        <CustomCreatableReactSelect
                                                            optionsArray={specialization}
                                                            placeholder={t("Enter specialization")}
                                                            setField={setFieldValue}
                                                            fieldName="specialization"
                                                            value="name"
                                                            isTooltip={true}
                                                            name={"specialization-field"}
                                                            id={"addOwner-specialization-field"}
                                                            maxLength={50}
                                                            createOptionLabel={t("Create a specialization")}
                                                            noOptionsText={t("Start typing the specialization name")}
                                                            createOnBlur={true}
                                                        />
                                                        <div
                                                            className="position-absolute top-50 end-0 translate-middle-y pe-6"
                                                            style={{ height: "16px" }}
                                                        >
                                                            <OverlayTrigger
                                                                overlay={
                                                                    <Tooltip>
                                                                        {t(
                                                                            "This data is displayed in the Online Booking form"
                                                                        )}
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <div className=" position-absolute top-50 translate-middle-y end-0 me-3">
                                                                    <i className="bi bi-info-circle"></i>
                                                                </div>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-sm-9 col-md-10 ms-auto">
                                                    <span className="text-grey fs-7">
                                                        {" "}
                                                        {values.specialization.length}/50
                                                    </span>
                                                    <ErrorMessage name="specialization">
                                                        {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}
                                                    </ErrorMessage>
                                                </div>
                                            </div>
                                            {/* Hide the access rights field on the owner creation page since it cannot be edited */}
                                            {/*<div className="row mt-sm-3">*/}
                                            {/*    <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">*/}
                                            {/*        <label*/}
                                            {/*            htmlFor="registration-role-field"*/}
                                            {/*            className="form-label mb-0 text-dark fw-bold required-label"*/}
                                            {/*        >*/}
                                            {/*            <small>{t("Editing Permissions")}</small>*/}
                                            {/*        </label>*/}
                                            {/*    </div>*/}
                                            {/*    <div className="col-sm-9 col-md-10 pe-lg-5">*/}
                                            {/*        <div className="position-relative">*/}
                                            {/*            <Field*/}
                                            {/*                id="editing-role-field"*/}
                                            {/*                type="text"*/}
                                            {/*                name="role-permissions"*/}
                                            {/*                placeholder={t("Owner")}*/}
                                            {/*                className="fs-7 form-control"*/}
                                            {/*                maxLength="50"*/}
                                            {/*                disabled*/}
                                            {/*            />*/}
                                            {/*            <div*/}
                                            {/*                className="position-absolute top-50 end-0 translate-middle-y pe-6"*/}
                                            {/*                style={{ height: "16px" }}*/}
                                            {/*            >*/}
                                            {/*                <OverlayTrigger*/}
                                            {/*                    overlay={*/}
                                            {/*                        <Popover>*/}
                                            {/*                            <Popover.Body>*/}
                                            {/*                                <Row>*/}
                                            {/*                                    <p className="m-0 mb-1">*/}
                                            {/*                                        {" "}*/}
                                            {/*                                        {t(*/}
                                            {/*                                            '"Editing permissions" - defines what access the employee has.'*/}
                                            {/*                                        )}{" "}*/}
                                            {/*                                    </p>*/}
                                            {/*                                </Row>*/}
                                            {/*                                <Row>*/}
                                            {/*                                    <p className="m-0 mb-1">*/}
                                            {/*                                        {" "}*/}
                                            {/*                                        {t(*/}
                                            {/*                                            "Owner - unlimited access to all sections."*/}
                                            {/*                                        )}*/}
                                            {/*                                    </p>*/}
                                            {/*                                </Row>*/}
                                            {/*                                <Row>*/}
                                            {/*                                    <p className="m-0 mb-1">*/}
                                            {/*                                        {t(*/}
                                            {/*                                            'Administrator - access to all sections except "Tariff Management."'*/}
                                            {/*                                        )}*/}
                                            {/*                                    </p>*/}
                                            {/*                                </Row>*/}
                                            {/*                                <Row>*/}
                                            {/*                                    <p className="m-0 mb-1">*/}
                                            {/*                                        {t(*/}
                                            {/*                                            "Employee - has limited rights, he can only view his schedule, his record log, information about himself and the services he performs."*/}
                                            {/*                                        )}{" "}*/}
                                            {/*                                    </p>*/}
                                            {/*                                </Row>*/}
                                            {/*                            </Popover.Body>*/}
                                            {/*                        </Popover>*/}
                                            {/*                    }*/}
                                            {/*                >*/}
                                            {/*                    <div className="position-absolute top-50 translate-middle-y end-0 me-3">*/}
                                            {/*                        <i className="bi bi-info-circle"></i>*/}
                                            {/*                    </div>*/}
                                            {/*                </OverlayTrigger>*/}
                                            {/*            </div>*/}
                                            {/*        </div>*/}

                                            {/*        <ErrorMessage name="role">*/}
                                            {/*            {(msg) => <p className="fs-7 m-0 p-1 text-danger">{msg}</p>}*/}
                                            {/*        </ErrorMessage>*/}
                                            {/*    </div>*/}
                                            {/*</div>*/}
                                            <div className="row mt-sm-4">
                                                <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                    <span className="text-dark fw-bold">
                                                        <small>
                                                            <label
                                                                htmlFor="registration-qualificationConfirmations-field"
                                                                className="mb-0 text-dark text-break form-label"
                                                            >
                                                                {t("Qualification Confirmation")}
                                                            </label>
                                                        </small>
                                                    </span>
                                                </div>

                                                <div className="col-sm-9 col-md-10 pe-lg-5">
                                                    <div className="mb-0 ps-sm-1 mb-2 text-dark">
                                                        <span className="fs-7">
                                                            {t(
                                                                "Add PNG, JPG, JPEG, WEBP images of diplomas, certificates"
                                                            )}{" "}
                                                        </span>
                                                        <OverlayTrigger
                                                            overlay={
                                                                <Tooltip>
                                                                    {t(
                                                                        "This data is displayed in the Online Booking form"
                                                                    )}
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <i className="bi bi-info-circle pe-6"></i>
                                                        </OverlayTrigger>
                                                    </div>
                                                    <Row className="m-0">
                                                        <DropPhotosMultiply
                                                            photosArray={photosQualification}
                                                            setPhotos={setPhotosQualification}
                                                        />
                                                    </Row>
                                                </div>
                                                <div className="col-sm-9 col-md-10 ms-auto">
                                                    <span className="text-grey fs-7"> {t("Maximum size: 3 MB")}</span>
                                                </div>
                                            </div>

                                            <div className="row mt-sm-4">
                                                <div className="col-sm-3 col-md-2 mt-2 pt-1 mb-2 mb-sm-0">
                                                    <span className="text-dark fw-bold">
                                                        <small>
                                                            <label
                                                                htmlFor="registration-contract-field"
                                                                className="mb-0 text-dark form-label"
                                                            >
                                                                {" "}
                                                                {t("Contract")}
                                                            </label>
                                                        </small>
                                                    </span>
                                                </div>

                                                <div className="col-sm-9 col-md-10 pe-lg-5">
                                                    <DropItem
                                                        index={0}
                                                        handleFile={setFieldValue}
                                                        fieldName={"contract"}
                                                        file={true}
                                                    />
                                                </div>
                                                <div className="col-sm-9 col-md-10 ms-auto mt-2">
                                                    <span className="text-grey fs-7">
                                                        {t("Document formats: PDF, DOC, DOCX")}
                                                    </span>
                                                </div>
                                            </div>
                                        </Col>

                                        <Col xs={12} lg={3} className="ps-lg-3 ps-0 mt-4 mt-lg-0">
                                            <h4 className="text-dark pb-3 mt-1">{t("Owner's photo")}</h4>
                                            <ImageCrop
                                                setFieldValue={setFieldValue}
                                                modalTitle={t("Owner's photo")}
                                                notBGinfo
                                            />
                                        </Col>
                                    </div>
                                    <div className="d-sm-flex mb-5 mt-3">
                                        <Button
                                            type="submit"
                                            variant="primary"
                                            className="fw-bold custom-btn rounded-1"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <SpinnerLoader variant="light" loadingText={t("Saving...")} />
                                            ) : (
                                                t("save")
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </Form>
                        </>
                    );
                }}
            </Formik>
            {isErrorModalOpen && (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            )}
        </>
    );
};

export default OwnerCreation;
